/**
 * ExteriorPreview — V14.62 Sprint 24 (Mode Maison Unifamiliale).
 *
 * Compose la vue extérieure de la maison :
 *   • Image satellite Google Maps Static (si l'adresse est connue)
 *   • Polygone du terrain dessiné par l'utilisateur (RDC)
 *   • Empreinte au sol du bâtiment (enveloppe convexe des murs)
 *   • Métriques : surface terrain, surface bâtie, hauteur totale, niveaux
 *
 * Si l'adresse n'est pas (encore) renseignée, on n'affiche QUE le terrain
 * + l'empreinte sur un fond neutre + une invitation à ajouter l'adresse
 * plus tard.
 *
 * La clé Google Maps est consommée côté frontend uniquement via
 * `lib/mapsKey.js` — le backend n'a pas besoin de cette clé.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Satellite, RefreshCcw, Loader2, AlertCircle, MapPin, Ruler, Layers,
  Home, Trees,
} from 'lucide-react';
import { GOOGLE_MAPS_API_KEY, HAS_GOOGLE_MAPS_API_KEY } from '../../lib/mapsKey';
import { formatApiError } from '../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SVG viewport dimensions — tuned for a 16:10 landscape preview.
const VBOX_W = 640;
const VBOX_H = 400;
const PAD = 32;  // inner padding (px in svg space)

/**
 * Normalise an array of {x,y} points into the SVG viewport, preserving
 * aspect ratio, centered. Returns the polyline `points` attribute and the
 * scale used (so we can render labels at consistent sizes).
 */
const fitPolygonsToViewport = (polygons) => {
  const all = [];
  polygons.forEach((p) => p && p.forEach((pt) => all.push(pt)));
  if (all.length === 0) {
    return { transform: (pt) => pt, scale: 1, dx: 0, dy: 0 };
  }
  const xs = all.map((p) => (Array.isArray(p) ? p[0] : p.x));
  const ys = all.map((p) => (Array.isArray(p) ? p[1] : p.y));
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);
  const sx = (VBOX_W - 2 * PAD) / w;
  const sy = (VBOX_H - 2 * PAD) / h;
  const scale = Math.min(sx, sy);
  const drawW = w * scale;
  const drawH = h * scale;
  const dx = (VBOX_W - drawW) / 2 - minX * scale;
  const dy = (VBOX_H - drawH) / 2 - minY * scale;
  return {
    transform: (pt) => {
      const x = Array.isArray(pt) ? pt[0] : pt.x;
      const y = Array.isArray(pt) ? pt[1] : pt.y;
      return [x * scale + dx, y * scale + dy];
    },
    scale, dx, dy,
  };
};

const polyAttr = (points, fit) =>
  points.map((p) => fit.transform(p).map((v) => v.toFixed(1)).join(',')).join(' ');

const formatNumber = (n, decimals = 1) => {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
};

const MetricCard = ({ icon: Icon, label, value, unit, testId, tone = 'emerald' }) => {
  const palette = tone === 'amber'
    ? { border: 'border-amber-400/40', bg: 'bg-amber-500/10',  text: 'text-amber-200',  icon: 'text-amber-400' }
    : tone === 'sky'
    ? { border: 'border-sky-400/40',   bg: 'bg-sky-500/10',    text: 'text-sky-200',    icon: 'text-sky-400' }
    : { border: 'border-emerald-400/40', bg: 'bg-emerald-500/10', text: 'text-emerald-200', icon: 'text-emerald-400' };
  return (
    <div data-testid={testId}
         className={`rounded-md border px-3 py-2 ${palette.border} ${palette.bg}`}>
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.2em] text-gray-400">
        <Icon className={`w-3 h-3 ${palette.icon}`} />
        {label}
      </div>
      <div className={`mt-0.5 font-mono text-sm font-semibold ${palette.text}`}>
        {value}
        <span className="text-[10.5px] text-gray-500 font-normal ml-1">{unit}</span>
      </div>
    </div>
  );
};

const ExteriorPreview = ({ houseId, projectId, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sprint 26E — supports two data sources :
  //   • Standalone /maison/:id editor      → /single-house/projects/{houseId}/exterior
  //   • Production Édition Plan flow       → /projects/{projectId}/maison/exterior
  // The second one reads from the project's lots/floors instead of the
  // legacy single_house_projects collection.
  const endpoint = projectId
    ? `${API}/projects/${projectId}/maison/exterior`
    : `${API}/single-house/projects/${houseId}/exterior`;

  const fetchExterior = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await axios.get(endpoint, { withCredentials: true });
      setData(payload);
    } catch (err) {
      setError(formatApiError(err, 'Calcul de la vue extérieure impossible'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!houseId && !projectId) return;
    fetchExterior();
  }, [houseId, projectId, refreshKey]);

  // Frontend-side satellite URL (the backend can't because the maps key is
  // only declared in the frontend env). Falls back to the backend value if
  // we ever wire one up server-side.
  const satelliteUrl = useMemo(() => {
    if (!data) return '';
    if (data.satellite_url) return data.satellite_url;
    if (!data.has_address || !HAS_GOOGLE_MAPS_API_KEY) return '';
    const address = encodeURIComponent(data.address || '');
    return `https://maps.googleapis.com/maps/api/staticmap`
      + `?center=${address}&zoom=19&size=${VBOX_W}x${VBOX_H}`
      + `&maptype=satellite&scale=2&key=${GOOGLE_MAPS_API_KEY}`;
  }, [data]);

  const terrainPts = data?.terrain?.terrain_px || [];
  const footprintPts = data?.footprint?.footprint_px || [];
  const hasTerrain = terrainPts.length >= 3;
  const hasFootprint = footprintPts.length >= 3;

  const fit = useMemo(
    () => fitPolygonsToViewport([terrainPts, footprintPts]),
    [terrainPts, footprintPts],
  );

  // Stack of floor labels for the right-hand info column.
  const extrusion = data?.extrusion?.layout || [];

  if (loading) {
    return (
      <div className="rounded-lg border p-6 flex items-center gap-2 text-gray-300 text-[12px]"
           style={{ borderColor: 'rgba(16,185,129,0.18)', background: 'rgba(2,6,12,0.6)' }}
           data-testid="exterior-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Composition de la vue extérieure…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border p-4 flex items-center gap-2 text-amber-300 text-[12px]"
           style={{ borderColor: 'rgba(251,191,36,0.4)', background: 'rgba(120,53,15,0.15)' }}
           data-testid="exterior-error">
        <AlertCircle className="w-4 h-4" />
        {error}
        <button type="button" onClick={fetchExterior}
                className="ml-auto inline-flex items-center gap-1 h-7 px-2.5 rounded border border-amber-400/50 text-amber-200 hover:bg-amber-500/10">
          <RefreshCcw className="w-3 h-3" /> Réessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  const showSatellite = !!satelliteUrl;
  const surfaceTerrain = data.terrain?.area_m2 || 0;
  const surfaceBatie = data.footprint?.surface_m2 || 0;
  const hauteurTotale = data.extrusion?.above_ground_height_m || 0;
  const nbNiveaux = (extrusion || []).filter((f) => f.level >= 0).length;

  return (
    <section className="rounded-lg border p-4 space-y-3"
             style={{ borderColor: 'rgba(16,185,129,0.18)', background: 'rgba(2,6,12,0.6)' }}
             data-testid="exterior-preview">

      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <Satellite className="w-4 h-4 text-emerald-400" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300/80">
            Vue extérieure 3D
          </div>
          <div className="text-[11px] text-gray-400 truncate" data-testid="exterior-address">
            {data.address
              ? <><MapPin className="inline w-3 h-3 mr-1 -mt-px text-emerald-400/70" />{data.address}</>
              : <span className="italic">Adresse non renseignée — terrain seul</span>}
          </div>
        </div>
        <button type="button" onClick={fetchExterior}
                data-testid="exterior-refresh-btn"
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded border border-white/15 text-[10.5px] text-gray-300 hover:bg-white/5 hover:border-emerald-400/40">
          <RefreshCcw className="w-3 h-3" /> Recalculer
        </button>
      </div>

      {/* Hint banners */}
      {!hasTerrain && (
        <div className="rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-200 px-3 py-2 text-[11px] flex items-start gap-2"
             data-testid="exterior-no-terrain-hint">
          <Trees className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>Aucun terrain dessiné.</strong> Passez sur le RDC, activez
            l&apos;outil <kbd className="px-1 py-0.5 rounded border border-amber-400/40 bg-black/30 font-mono">T</kbd> (Terrain)
            et cliquez chaque sommet pour tracer la limite de propriété. Double-clic
            ou Entrée pour fermer le polygone.
          </div>
        </div>
      )}
      {hasTerrain && !data.terrain.scale_m_per_px && (
        <div className="rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-200 px-3 py-2 text-[11px] flex items-center gap-2"
             data-testid="exterior-no-scale-hint">
          <Ruler className="w-3.5 h-3.5" />
          Le RDC n&apos;est pas calibré (outil Règle <kbd className="px-1 py-0.5 rounded border border-amber-400/40 bg-black/30 font-mono">C</kbd>) —
          les surfaces ne peuvent pas être exprimées en m².
        </div>
      )}

      {/* Canvas : satellite (if any) + SVG overlay */}
      <div className="relative w-full rounded-md overflow-hidden border"
           style={{ borderColor: 'rgba(16,185,129,0.25)', aspectRatio: `${VBOX_W} / ${VBOX_H}`,
                    background: showSatellite ? '#0a0f18' : 'linear-gradient(135deg,#0b1322 0%,#0a1f1a 100%)' }}
           data-testid="exterior-canvas">

        {showSatellite ? (
          <img
            src={satelliteUrl}
            alt={`Vue satellite ${data.address}`}
            data-testid="exterior-satellite-img"
            className="absolute inset-0 w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          // Subtle grid background to ground the polygons visually.
          <svg className="absolute inset-0 w-full h-full" aria-hidden="true">
            <defs>
              <pattern id="grid-bg" width="24" height="24" patternUnits="userSpaceOnUse">
                <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(16,185,129,0.12)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-bg)" />
          </svg>
        )}

        {/* SVG overlay — vectors on top, always rendered so they show
            against either the satellite image or the neutral grid. */}
        <svg
          viewBox={`0 0 ${VBOX_W} ${VBOX_H}`}
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 w-full h-full"
          data-testid="exterior-svg-overlay"
        >
          {/* Terrain — sand-yellow dashed outline + translucent green fill */}
          {hasTerrain && (
            <polygon
              points={polyAttr(terrainPts, fit)}
              fill="rgba(34,197,94,0.18)"
              stroke="#fbbf24"
              strokeWidth="2.5"
              strokeDasharray="10 6"
              strokeLinejoin="round"
              data-testid="exterior-terrain-polygon"
            />
          )}
          {/* Footprint — solid emerald outline + filled silhouette */}
          {hasFootprint && (
            <polygon
              points={polyAttr(footprintPts, fit)}
              fill="rgba(16,185,129,0.45)"
              stroke="#34d399"
              strokeWidth="2"
              strokeLinejoin="round"
              data-testid="exterior-footprint-polygon"
            />
          )}
          {/* Empty state — when both polygons are missing */}
          {!hasTerrain && !hasFootprint && (
            <text x={VBOX_W / 2} y={VBOX_H / 2}
                  textAnchor="middle"
                  fill="rgba(229,231,235,0.5)"
                  fontFamily="system-ui, sans-serif"
                  fontSize="14"
                  data-testid="exterior-empty-state">
              Tracez murs et terrain pour générer la vue
            </text>
          )}
        </svg>

        {/* Legend overlay */}
        <div className="absolute bottom-2 left-2 flex items-center gap-2 text-[10px] font-mono"
             data-testid="exterior-legend">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-black/55 border border-white/10 text-amber-200">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'rgba(34,197,94,0.18)', borderTop: '2px dashed #fbbf24' }} />
            Terrain
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-black/55 border border-white/10 text-emerald-200">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'rgba(16,185,129,0.5)', border: '1px solid #34d399' }} />
            Empreinte bâtie
          </span>
        </div>
        {showSatellite && (
          <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/55 border border-white/10 text-[10px] text-gray-300 font-mono"
               data-testid="exterior-satellite-badge">
            Google Maps · Satellite
          </div>
        )}
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2"
           data-testid="exterior-metrics">
        <MetricCard icon={Trees} tone="amber" label="Surface terrain"
                    value={surfaceTerrain ? formatNumber(surfaceTerrain) : '—'}
                    unit="m²" testId="metric-terrain-area" />
        <MetricCard icon={Home}  tone="emerald" label="Empreinte bâtie"
                    value={surfaceBatie ? formatNumber(surfaceBatie) : '—'}
                    unit="m²" testId="metric-footprint-area" />
        <MetricCard icon={Ruler} tone="sky" label="Hauteur totale"
                    value={hauteurTotale ? formatNumber(hauteurTotale, 2) : '—'}
                    unit="m" testId="metric-total-height" />
        <MetricCard icon={Layers} tone="emerald" label="Niveaux"
                    value={nbNiveaux || '—'}
                    unit={nbNiveaux > 1 ? 'étages' : 'étage'} testId="metric-floor-count" />
      </div>

      {/* Floor stack — vertical bar showing where each floor sits */}
      {extrusion.length > 0 && (
        <div className="rounded-md border border-white/10 bg-black/30 px-3 py-2"
             data-testid="exterior-stack-summary">
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-gray-400 mb-1">
            Empilement vertical
          </div>
          <div className="flex flex-col-reverse gap-0.5">
            {extrusion.map((f) => (
              <div key={f.floor_id}
                   data-testid={`exterior-floor-${f.floor_id}`}
                   className={`flex items-center justify-between text-[10.5px] font-mono px-2 py-0.5 rounded ${
                     f.level >= 0 ? 'bg-emerald-500/10 text-emerald-200' : 'bg-sky-500/10 text-sky-200'
                   }`}>
                <span className="truncate">
                  {f.name}{' '}
                  <span className="opacity-50">(niveau {f.level >= 0 ? `+${f.level}` : f.level})</span>
                </span>
                <span className="opacity-80">
                  {f.bottom_z.toFixed(2)} → {f.top_z.toFixed(2)} m
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ExteriorPreview;
